import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Modal, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { ModalRowsSkeleton } from '@/components/profile';
import { OPERATION, ROLES_PERMISSIONS, ROUTES } from '@/constants';
import PrivateAction from '@/routes/PrivateAction';
import { API } from '@/services/api.service';
import { QUERY_KEY } from '@/shared/constants';
import { DATE_FORMATS } from '@/utils/formUtils';

const initials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

const keyOf = (v) => (v && typeof v === 'object' ? v.key : v);

const personName = (p) =>
  [p?.firstName, p?.lastName].filter(Boolean).join(' ') || p?.email || '';

function Row({ label, children, wide }) {
  return (
    <div
      className={`flex items-start gap-2.5 border-b border-zinc-100 py-2 text-sm ${wide ? 'col-span-2' : ''}`}
    >
      <span className="w-[150px] shrink-0 truncate whitespace-nowrap text-[13px] text-zinc-500">
        {label}
      </span>
      <span className="min-w-0 flex-1 font-semibold">{children || '—'}</span>
    </div>
  );
}

/**
 * Read-only summary of the employee's company, opened from the company name in
 * the profile header. "Edit company" leads to the company page in edit mode.
 */
export default function CompanyPreviewModal({ companyId, open, onClose }) {
  const { t } = useTranslation(['translations', 'forms', 'menus']);
  const tp = (key, opts) =>
    t(`employeeProfile.${key}`, { ns: 'translations', ...opts });

  // The full company (no graph): officers and sectors come back with names.
  const { data: company, isLoading } = useQuery({
    queryKey: [QUERY_KEY.companiesData, companyId, 'profile-preview'],
    queryFn: () => API.companies.getCompanyById(companyId),
    enabled: open && !!companyId,
  });

  // Headcount; roles without company statistics simply get no number.
  const { data: stats } = useQuery({
    queryKey: [QUERY_KEY.companiesData, companyId, 'profile-stats'],
    queryFn: () => API.companies.getCompanyProfileStats(companyId),
    enabled: open && !!companyId,
    retry: false,
  });

  const app = company?.applicationSettings || {};
  const gd = company?.generalData || {};
  // Parts may be stored as "undefined"/"null" text; only digits count.
  const account = [
    gd.bankAccount?.part1,
    gd.bankAccount?.part2,
    gd.bankAccount?.part3,
  ]
    .map((part) => String(part ?? '').trim())
    .filter((part) => /^\d+$/.test(part))
    .join('-');
  const sectorNames = (company?.sectors || [])
    .map((s) => (typeof s === 'object' ? s.sectorName : null))
    .filter(Boolean);
  const officers = (company?.clientsAdmins?.clientOfficers || [])
    .map(personName)
    .filter(Boolean);
  const typeOfHr = keyOf(app.typeOfHr);
  const returnType = keyOf(app.documentReturnType);
  const modules = [
    app.isHr && 'HR',
    app.eInvoice && 'e-Faktura',
    app.timeTracking && tp('moduleTimeTracking'),
    app.isSector && tp('moduleSectors'),
    app.weekendLeaves && tp('moduleWeekendLeaves'),
    app.foreignWorkPermitTracker && tp('moduleWorkPermits'),
  ].filter(Boolean);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
      closable={false}
      classNames={{ content: '!p-0 overflow-hidden !rounded-[18px]' }}
      destroyOnHidden
    >
      <div className="flex items-center justify-between gap-3 bg-accent px-5 py-4 text-white">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white font-extrabold text-accent">
            {initials(company?.name)}
          </div>
          <div>
            <div className="text-lg font-extrabold">{company?.name}</div>
            <div className="text-[13px] opacity-90">
              {[gd.code && `MB ${gd.code}`, gd.pib && `PIB ${gd.pib}`]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
        </div>
        <button
          type="button"
          aria-label={tp('close')}
          onClick={onClose}
          className="h-8 w-8 cursor-pointer rounded-full border-none bg-white/20 text-white"
        >
          ×
        </button>
      </div>

      {isLoading ? (
        <ModalRowsSkeleton rows={12} />
      ) : (
        <div className="grid grid-cols-2 gap-x-6 px-5 pt-3">
          <Row label={tp('address')}>
            {[gd.address, [gd.zip, gd.city].filter(Boolean).join(' ')]
              .filter(Boolean)
              .join(', ')}
          </Row>
          <Row label={t('market.label', { ns: 'forms' })}>
            {company?.market
              ? t(`market.${company.market}`, { ns: 'forms' })
              : null}
          </Row>
          <Row label={tp('type')}>
            {keyOf(app.typeOfCompany) === 'branch'
              ? tp('branch')
              : tp('mainCompany')}
          </Row>
          <Row label={tp('employees')}>
            {stats ? tp('activeEmployees', { count: stats.activeCount }) : null}
          </Row>
          <Row label={t('email', { ns: 'forms' })}>{gd.email}</Row>
          <Row label={t('phoneNumber', { ns: 'forms' })}>{gd.phoneNumber}</Row>
          <Row label={t('bankAccountNumber', { ns: 'forms' })}>{account}</Row>
          <Row label={t('clientOfficer', { ns: 'forms' })}>
            {officers.join(', ')}
          </Row>
          <Row label={tp('documentsFormat')}>
            {[
              typeOfHr === 'bilingual'
                ? tp('docsBilingual')
                : typeOfHr === 'serbian'
                  ? tp('docsSerbian')
                  : typeOfHr,
              returnType === 'doc' ? 'Word' : returnType?.toUpperCase(),
            ]
              .filter(Boolean)
              .join(' · ')}
          </Row>
          <Row label={tp('holidayTracker')}>
            {app.holidayDaysTracker
              ? app.trackerStartDate
                ? tp('trackerSince', {
                    date: dayjs(app.trackerStartDate).format(
                      DATE_FORMATS.DISPLAY
                    ),
                  })
                : 'HR'
              : tp('no')}
          </Row>
          <Row label={tp('sectors')} wide>
            {sectorNames.length ? (
              <Tooltip title={sectorNames.join(', ')}>
                <span className="block truncate">
                  {sectorNames.length} · {sectorNames.join(', ')}
                </span>
              </Tooltip>
            ) : null}
          </Row>
          <Row label={tp('modules')} wide>
            {modules.join(' · ')}
          </Row>
        </div>
      )}

      <div className="flex items-center gap-1.5 whitespace-nowrap px-5 pt-3 text-xs text-zinc-500">
        <InfoCircleOutlined />
        <span className="truncate">{tp('infoCompanyPreview')}</span>
      </div>
      <div className="flex justify-end gap-2 px-5 pb-5 pt-3">
        <Button className="btn-default btn-default--hover" onClick={onClose}>
          {tp('close')}
        </Button>
        <PrivateAction {...ROLES_PERMISSIONS.COMPANIES.EDIT}>
          <Link
            to={`${ROUTES.admin}/${ROUTES.companies}/${OPERATION.edit}/${companyId}`}
          >
            <Button type="primary" className="btn-primary">
              {tp('editCompany')} →
            </Button>
          </Link>
        </PrivateAction>
      </div>
    </Modal>
  );
}
